// ---------------------------------------------------------
// Datei: ToastContext.tsx
// Zweck: Globaler Toast-State für App-weite Notifications
// Besonderheiten:
// - Max 3 Toasts gleichzeitig (älteste wird entfernt)
// - Toast-Container fixed bottom-right (Mobile: bottom-center)
// - Safe-Area-Insets für iOS/Android
// - Auto-ID-Generation mit crypto.randomUUID()
// ---------------------------------------------------------

import React, { createContext, useContext, useState, useCallback } from 'react';
import Toast from '../components/Toast';
import type { ToastType } from '../types/ui';
import type { ToastData, ToastContextType } from '../interfaces/ui';

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastData[]>([]);
  
  const showToast = useCallback((type: ToastType, message: string, duration?: number) => {
    const id = crypto.randomUUID();
    const newToast: ToastData = { id, type, message, duration };
    
    setToasts(prev => {
      // Identische Meldung, die gerade noch sichtbar ist, nicht erneut stapeln (AB#1474:
      // doppelte "konnte nicht geladen werden"-Toasts).
      if (prev.some(toast => toast.type === type && toast.message === message)) {
        return prev;
      }
      // Max 3 Toasts gleichzeitig
      const updated = [...prev, newToast];
      return updated.length > 3 ? updated.slice(-3) : updated;
    });
  }, []);
  
  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);
  
  return (
    <ToastContext.Provider value={{ showToast, removeToast }}>
      {children}
      
      {/* Toast-Container */}
      <div
        className="
          fixed
          bottom-[calc(env(safe-area-inset-bottom)+80px)]
          right-4
          z-[70]
          flex flex-col items-end
          sm:bottom-[calc(env(safe-area-inset-bottom)+16px)]
          sm:right-4
        "
        aria-live="polite"
        aria-atomic="false"
      >
        {toasts.map(toast => (
          <Toast
            key={toast.id}
            id={toast.id}
            type={toast.type}
            message={toast.message}
            duration={toast.duration}
            onClose={removeToast}
          />
        ))}
      </div>
    </ToastContext.Provider>
  );
};

// Hook für einfache Nutzung
export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
};
